import { Typography } from '@mui/material';
import { useUser } from '@providers/User';
import { GA } from '@services/gtag';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Toast } from 'src/components/Toast';
import Toggle from 'src/components/Toggle';
import { useConfiguration } from 'src/providers/Configuration';
import { useTenant } from 'src/providers/Tenant';
import { Configuration, patchConfiguration } from 'src/services/productInventory';

interface ProductStatusToggleProps {
  configurationPID: string;
  productName: string;
}

export function ProductStatusToggle({ configurationPID, productName }: Readonly<ProductStatusToggleProps>) {
  const { accountId } = useTenant();
  const queryClient = useQueryClient();
  const { status } = useConfiguration();
  const { t } = useTranslation('manageProduct');
  const { username, role } = useUser();

  const isProductLive = status === 'LIVE';

  const {
    mutate: handleStatus,
    isLoading: isLoadingStatus,
    isError: isErrorStatus,
  } = useMutation(
    async (body: Partial<Configuration & { status: 'LIVE' | 'DRAFT' }>) => {
      await patchConfiguration(configurationPID, body, accountId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['getConfiguration']);
        queryClient.invalidateQueries(['productList']);
      },
    }
  );

  const handleStatusToggleChange = async (e: MouseEvent) => {
    const newStatus = (e.target as HTMLInputElement).checked ? 'LIVE' : 'DRAFT';
    handleStatus({ status: newStatus });

    // GTag
    const eventData = {
      account: accountId,
      user: username!,
      role: role!,
      productName,
      configurationPID,
    };

    if (newStatus === 'LIVE') {
      GA.ManageProductPage.productStatusDraftToLive(eventData);
    } else {
      GA.ManageProductPage.productStatusLiveToDraft(eventData);
    }
  };

  return (
    <>
      <Toggle
        loading={isLoadingStatus}
        checked={isProductLive}
        label={t('productOverview.productStatus')}
        states={{ checkedState: 'Live', unCheckedState: 'Draft' }}
        onClick={handleStatusToggleChange}
        id="productStatus"
      />
      {isProductLive ? (
        <>
          <Typography>{t('productOverview.productLive')}</Typography>
          <Typography>{t('productOverview.productSetToDraft')}</Typography>
        </>
      ) : (
        <>
          <Typography>{t('productOverview.productDraft')}</Typography>
          <Typography>{t('productOverview.productSetToLive')}</Typography>
        </>
      )}
      <Toast dependency={isErrorStatus} severity="error" title={t('productOverview.productUpdateFailed')} />
    </>
  );
}
